/**
 * What a cue from beat 2 *means*, in a scene made of triangles.
 *
 * Same seam as `bornDirector.ts`: `src/story/act1/weakestLatch.ts` says "the
 * round begins", and this file is the only thing that knows a worker is a pair
 * of boots, and that a gate and a door are leaves on hinges. Keeping those apart is what lets the beat replay in Node with no
 * renderer.
 *
 * ── The one line that differs from beat 1, and it is the whole of D-044 ──────
 *
 * Beat 1's boots are *moved* here, on the render step, and that is correct there
 * because nothing reads them. **Beat 2's worker is not moved here.** He is
 * advanced by a `Worker` in the fixed step, because he feeds a vision cone and a
 * suspicion meter and is therefore simulation state; this file only *reads* his
 * position and puts a mesh on it. Same for the gate: its open fraction is
 * simulation, its swing is presentation.
 *
 * If you ever find yourself writing `worker.step(dt)` in this file, stop. That is
 * the exact mistake D-044 was written to prevent, and it does not fail loudly —
 * it produces a worker whose cone samples at display rate, which is invisible on
 * the machine you are testing on and unreproducible in the golden trace.
 */

import * as THREE from 'three';
import { LATCH_CUES } from '@/story/act1/weakestLatch';
import { findWorkerParts, type WorkerParts } from '@/models/env/worker';
import { FadeOverlay } from '@/world/fadeOverlay';
import { penGateLeaf } from '@/models/env/pen-gate';
import type { Cue } from '@/story/types';
import type { WorkerState } from '@/systems/worker';
import { latchWorkerRoute } from '@/world/latchScene';

const LATCH_STRIDE = latchWorkerRoute().stride;

/**
 * How fast the gate swings once she has worked the latch off its keeper.
 *
 * Slow — a heavy gate on a dropped catch does not fly open, it leans. And the
 * slowness is load-bearing rather than decorative: the swing is the only
 * confirmation she gets that the verb worked, there is no HUD to say so (spec
 * §5.1), and a gate that snapped open would read as a door being unlocked for
 * her rather than as something giving under her weight.
 */
const GATE_SWING_RATE = 3.3;

/** How far it ends up. Not a full quarter turn — it fouls on the drain lip. */
export const GATE_OPEN_MAX = 0.86;

/**
 * How far it has to have swung before she can get through.
 *
 * Measured against the leaf, not guessed: at 0.30 the gap between the free edge
 * and the mouth's near post is wider than her 0.27 m across the shoulders with
 * room to turn. The beat raises `gateOpen` at this point rather than at
 * `GATE_OPEN_MAX`, so the window does not spend two seconds waiting for a mesh
 * to finish moving.
 */
export const GATE_PASSABLE = 0.3;

/** `window.__rosalie.latchDebug`: beat 2's worker, gate and stealth field. */
export interface LatchDebug {
  readonly worker: { x: number; z: number; facing: number; phase: string; waypoint: number };
  /** Rounds finished. The window is his dwell at the far trough. */
  roundsCompleted: number;
  /**
   * Whether beat 1's taken littermate is back in the straw.
   *
   * Beat 2's one continuity invariant, and it lives here rather than in
   * `beatDebug` because `beatDebug` is beat 1's own view and is null when beat
   * 1 is not running — a spec reading it during beat 2 gets `null` and a
   * fallback, which is how this assertion passed while being wrong.
   */
  littermateBack: boolean;
  /** 0 shut, 1 fully swung. Simulation, not presentation. */
  gateOpen: number;
  /** Whether she can fit through yet. What the beat's `gateOpen` flag is. */
  gatePassable: boolean;
  /**
   * The walkway door's angle, radians: 0 shut, `WALKWAY_DOOR_AJAR` once he
   * has been through it. Simulation, as the gate's is.
   */
  doorAngle: number;
  /** 0..100 for the one watcher, and his state word. No HUD shows this. */
  suspicion: number;
  watcherState: string;
  /** How visible she is RIGHT NOW, 0..1, and the four terms whose product it is. */
  visibility: number;
  contributions: { angle: number; distance: number; exposure: number; lineOfSight: number };
  /**
   * Why she was last seen, or null.
   *
   * Spec §5.1 requires every detection to be explainable after the fact. This
   * is that explanation in machine-readable form: the four contributions whose
   * product is the visibility, and the sound that pushed him over if one did.
   */
  lastDetection: {
    angle: number;
    distance: number;
    exposure: number;
    lineOfSight: number;
  } | null;
  fade: number;
}

export interface LatchDirectorParts {
  /**
   * The full worker figure — a `worker` asset with its rig intact.
   *
   * **Not beat 1's merged boots.** `bornBay.ts` builds those with `collapse()`,
   * which is what makes them one draw call and also what leaves them with no
   * pivots to turn; `findWorkerParts` throws on them, by name, immediately.
   * `main.ts` swaps the mesh before constructing this class.
   */
  worker: THREE.Object3D;
  /**
   * The walkway door's `door-hinge`. Its angle is simulation (the fixed step
   * moves its collider); this only turns the picture to it.
   */
  doorHinge: THREE.Object3D;
  /** The gate leaf, parented so that its own origin is the hinge. */
  gate: THREE.Object3D;
  /** Where the gate hinges, world XZ. */
  gateHinge: { x: number; z: number };
  /** Which way it opens. See `penGateLeaf`. */
  gateSwing: -1 | 1;
}

export interface LatchDirectorOptions {
  /** Called when a boot lands, with the world position it landed at. */
  onBootfall(at: { x: number; y: number; z: number }): void;
  /** Called once, when the gate first comes off its keeper. */
  onGateGiven?(): void;
}

export class LatchDirector {
  readonly #parts: LatchDirectorParts;
  readonly #options: LatchDirectorOptions;
  readonly #fadeOverlay: FadeOverlay;
  readonly #workerParts: WorkerParts;
  #distanceTravelled = 0;
  #previousWorker: { x: number; z: number } | null = null;

  /**
   * How far the gate has swung, 0..`GATE_OPEN_MAX`.
   *
   * **This is simulation state living in a presentation object, and it is the
   * one thing in this file that is allowed to be.** The stealth system does not
   * read it, but the physics collider does, and the beat's `gateOpen` flag is
   * raised from it — so `main.ts` advances it from `fixedUpdate` by calling
   * `stepGate`, and `update` only reads it. Splitting it into its own module for
   * the sake of the rule would put a two-field class between two callers that
   * are both in the same fixed step.
   */
  #gateOpen = 0;
  #gateTarget = 0;
  #gateGiven = false;

  #ended = false;

  constructor(parts: LatchDirectorParts, options: LatchDirectorOptions) {
    this.#parts = parts;
    this.#options = options;
    this.#fadeOverlay = new FadeOverlay();
    // Resolved, not built. The first version of this created the figure HERE and
    // swapped `parts.worker` out of the scene graph inside the constructor —
    // which worked, and is the wrong shape: a director that silently replaces an
    // object it was handed makes its caller's variable stale, and `main.ts` two
    // hundred lines away still had a comment explaining that it was reusing beat
    // 1's boots. `main.ts` owns scene assembly and already does exactly this swap
    // for the pen gate a dozen lines above where it builds this class.
    this.#workerParts = findWorkerParts(parts.worker);
  }

  /** True once the closing cue has fired. */
  get ended(): boolean {
    return this.#ended;
  }

  /** How black the screen is, 0..1. Exposed for the harness. */
  get fade(): number {
    return this.#fadeOverlay.value;
  }

  /** How far the gate has swung, 0..1. Simulation reads this. */
  get gateOpen(): number {
    return this.#gateOpen;
  }

  /** True once she can fit through. The beat's `gateOpen` flag is this. */
  get gatePassable(): boolean {
    return this.#gateOpen >= GATE_PASSABLE;
  }

  handle(cue: Cue): void {
    switch (cue.id) {
      case LATCH_CUES.open:
        this.#fadeOverlay.snapToBlackAndFadeIn();
        break;
      case LATCH_CUES.fadeOut:
        this.#fadeOverlay.setTarget(1);
        break;
      case LATCH_CUES.end:
        this.#ended = true;
        break;
      default:
        // Unknown cues are ignored rather than thrown on. A beat that emits
        // something this file has not learned yet should not take the render
        // step down; it should simply not be staged.
        break;
    }
  }

  /**
   * She has worked the latch. Called by the game when the hold completes.
   *
   * Idempotent: the interaction system completes a hold exactly once, but the
   * beat can also be skipped into this stage by the harness, and a gate that
   * restarted its swing every call would never arrive.
   */
  release(): void {
    if (this.#gateGiven) return;
    this.#gateGiven = true;
    this.#gateTarget = GATE_OPEN_MAX;
    this.#options.onGateGiven?.();
  }

  /**
   * Advance the gate. **Called from `fixedUpdate`, not from `update`.**
   *
   * Its collider moves with it and a collider that moves at display rate is a
   * collider she can walk through on a fast machine and not on a slow one.
   */
  stepGate(dt: number): number {
    const step = Number.isFinite(dt) ? Math.max(0, Math.min(0.1, dt)) : 0;
    this.#gateOpen += (this.#gateTarget - this.#gateOpen) * (1 - Math.exp(-GATE_SWING_RATE * step));
    if (this.#gateTarget - this.#gateOpen < 0.001) this.#gateOpen = this.#gateTarget;
    return this.#gateOpen;
  }

  /**
   * Runs on the render step. Presentation only.
   *
   * `worker` and `doorAngle` are the simulation's state, passed in rather
   * than stepped here. See the file header.
   */
  update(dt: number, worker: WorkerState, bootfalls: number, doorAngle: number): void {
    const frameDt = Number.isFinite(dt) ? Math.max(0, Math.min(0.1, dt)) : 0;

    this.#parts.worker.position.set(worker.x, 0, worker.z);
    // The mesh is authored facing +Z, which is the same convention the worker's
    // facing uses (0 is +Z, increasing toward +X) — so this is an assignment and
    // not a conversion, and if somebody later re-authors the boots to face -Z
    // this is the line that will be wrong. `worker.ts` says which way it faces;
    // believe that file, not this comment.
    this.#parts.worker.rotation.y = worker.facing;
    this.#parts.worker.visible = worker.phase !== 'parked';
    this.#animateWorker(worker, frameDt);

    // ── `bootfalls`, not `worker.footfall`, and it is the same bug twice ──────
    //
    // `WorkerState.footfall` is true on EXACTLY ONE fixed step, by design. But
    // this runs on the render step, so reading the flag directly is a level
    // sampled across an unrelated clock, and it fails in both directions: on a
    // 144 Hz display several frames see the same true and the boot lands three
    // times, and on a slow one two fixed steps fall between frames and a step is
    // silently lost. Found on independent review.
    //
    // `main.ts` already solves precisely this for `landingImpact` — it latches
    // the value across frames and the comment there says why. So the same shape:
    // the fixed step counts, the render step is handed the count and clears it.
    //
    // The count is collapsed to one sound rather than played out. More than one
    // stride between rendered frames means the display is slower than a walking
    // man, and two boot sounds fired at the same instant from the same position
    // are not two footsteps — they are one footstep and some mud.
    if (bootfalls > 0) {
      this.#options.onBootfall({ x: worker.x, y: 0, z: worker.z });
    }

    this.#parts.doorHinge.rotation.y = doorAngle;

    // Position AND rotation, because the leaf's centre orbits the hinge — it is
    // half a metre from the pivot, so a gate that only rotated would spin about
    // its own middle like a turnstile and leave the hinge stile swinging free in
    // the mouth. Both come from `penGateLeaf`, which is the same call the fixed
    // step makes to move the collider.
    //
    // Y is 0 rather than `leaf.pos.y`, and that is not an oversight: the collider
    // wants the box's centre and this is a ground-origin kit asset. See the note
    // in `pen-gate.ts` — the invariant that keeps the two agreeing is enforced by
    // `asset.spec.ts`, not by this line.
    const leaf = penGateLeaf(this.#parts.gateHinge, this.#parts.gateSwing, this.#gateOpen);
    this.#parts.gate.position.set(leaf.pos.x, 0, leaf.pos.z);
    this.#parts.gate.rotation.y = leaf.rotY;

    this.#fadeOverlay.update(frameDt);
  }

  dispose(): void {
    this.#fadeOverlay.dispose();
  }

  #animateWorker(worker: WorkerState, frameDt: number): void {
    const previous = this.#previousWorker;
    const distance = previous === null ? 0 : Math.hypot(worker.x - previous.x, worker.z - previous.z);
    this.#previousWorker = { x: worker.x, z: worker.z };
    if (worker.phase === 'walking' || worker.phase === 'returning') this.#distanceTravelled += distance;

    const walking = (worker.phase === 'walking' || worker.phase === 'returning') && distance > 0.0001;
    // One authored route stride is half a gait cycle, matching the simulation's
    // footfall distance instead of letting a render-clock gait drift from sound.
    const swing = walking ? Math.sin((this.#distanceTravelled / LATCH_STRIDE) * Math.PI) : 0;
    const settle = 1 - Math.exp(-8 * frameDt);
    const pose = (node: THREE.Object3D, target: number): void => {
      // 8/s avoids a stopped leg snapping upright between two render frames.
      node.rotation.x += (target - node.rotation.x) * settle;
    };
    pose(this.#workerParts.hips[0], swing * 0.42);
    pose(this.#workerParts.hips[1], -swing * 0.42);
    pose(this.#workerParts.shoulders[0], -swing * 0.26);
    pose(this.#workerParts.shoulders[1], swing * 0.26);
  }
}
